/**
 * @file Per-app configuration for Google Analytics (GA4).
 * Collects the user's selected accounts and properties, then patches
 * `custom_data` on the credential.
 *
 * Renders either as a post-OAuth modal (`variant="dialog"`, the default) or
 * inline inside the Settings sub-tab (`variant="inline"`), where it pre-loads
 * the saved accounts/properties.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  MultiSelect,
  toast,
} from "@strivelabs/ui-core";
import { executeAppAction, BASE_URL } from "@/services/DataService";
import { useCredentials, usePatchCredentialCustomData } from "@/hooks/useConnections";
import { logger } from "@/lib/logger";
import { InlineConfigPanel } from "@/components/settings/InlineConfigPanel";
import type { ConnectionsCardData, PostConnectionConfig } from "@/types/connections";

interface GoogleAnalyticsConfigDialogProps {
  /** Modal open state — used by the dialog variant only. */
  open?: boolean;
  config: PostConnectionConfig;
  onComplete: () => void;
  /** 'dialog' (default) renders a modal; 'inline' renders an embedded panel. */
  variant?: "dialog" | "inline";
}

interface GA4Account {
  id: string;
  name: string;
}

interface GA4Property {
  id: string;
  name: string;
  accountId: string;
}

/** Shape of a single account from the GA4 list_accounts response. */
interface GA4AccountRaw {
  name: string;
  displayName: string;
  createTime: string;
  updateTime: string;
  regionCode: string;
}

/** Shape of a single property from the GA4 list_properties response. */
interface GA4PropertyRaw {
  name: string;
  displayName: string;
  account: string;
  parent: string;
  createTime: string;
  updateTime: string;
  currencyCode: string;
  timeZone: string;
  industryCategory: string;
  propertyType: string;
  serviceLevel: string;
}

/** Shape of the executeAppAction response. */
interface ActionResponse<T> {
  status: string;
  items: Array<{
    scenario: string;
    data: T;
  }>;
}

function parseAccountsResponse(res: unknown): GA4Account[] {
  const response = res as ActionResponse<{ accounts: GA4AccountRaw[] }>;
  const accounts = response?.items?.[0]?.data?.accounts ?? [];

  return accounts.map((acc) => ({
    id: acc.name,
    name: acc.displayName || acc.name,
  }));
}

function parsePropertiesResponse(res: unknown, accountId: string): GA4Property[] {
  const response = res as ActionResponse<{ properties: GA4PropertyRaw[] }>;
  const properties = response?.items?.[0]?.data?.properties ?? [];

  return properties.map((prop) => ({
    id: prop.name,
    name: prop.displayName || prop.name,
    accountId,
  }));
}

/** Order-independent set equality for id lists. */
function sameIdSet(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((id) => b.includes(id));
}

/** Saved ids (accounts or properties) read from the credential's custom_data. */
function savedIds(cred: ConnectionsCardData | undefined, key: "accounts" | "properties"): string[] {
  const raw = (cred?.custom_data?.[key] ?? []) as Array<{ id?: string }>;
  return raw.map((x) => x?.id).filter((id): id is string => typeof id === "string");
}

export function GoogleAnalyticsConfigDialog({
  open,
  config,
  onComplete,
  variant = "dialog",
}: GoogleAnalyticsConfigDialogProps) {
  const isInline = variant === "inline";
  // The form is "active" (fetches data) whenever the modal is open, or always
  // for the inline panel (which only mounts when its sub-tab is shown).
  const active = isInline || !!open;

  const [accounts, setAccounts] = useState<GA4Account[]>([]);
  const [selectedAccountIds, setSelectedAccountIds] = useState<string[]>([]);
  const [properties, setProperties] = useState<GA4Property[]>([]);
  const [selectedPropertyIds, setSelectedPropertyIds] = useState<string[]>([]);

  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  const [propertiesError, setPropertiesError] = useState<string | null>(null);

  const patchCustomData = usePatchCredentialCustomData();
  const { data: credentialsData, isSuccess: credentialsLoaded } = useCredentials();

  /** The currently-saved selection — used to pre-load the inline panel. */
  const savedCredential = useMemo(
    () => (credentialsData?.credentials ?? []).find((c: ConnectionsCardData) => c.id === config.credentialId),
    [credentialsData, config.credentialId]
  );
  const savedAccountIds = useMemo(() => savedIds(savedCredential, "accounts"), [savedCredential]);
  const savedPropertyIds = useMemo(() => savedIds(savedCredential, "properties"), [savedCredential]);

  // Holds the property ids to restore once the properties list finishes loading
  // (inline pre-load). Only ever set in the inline flow.
  const pendingPropertyRef = useRef<string[] | null>(null);

  const fetchAccounts = useCallback(async () => {
    setLoadingAccounts(true);
    setAccountsError(null);
    try {
      const res = await executeAppAction(
        BASE_URL,
        "google_analytics",
        "GOOGLE_ANALYTICS_LIST_ACCOUNTS_V1_BETA",
        [{ id: config.credentialId }]
      );
      const parsed = parseAccountsResponse(res);
      if (parsed.length === 0) {
        setAccountsError("No accounts found. Please check your Google Analytics access.");
      }
      setAccounts(parsed);
    } catch (err) {
      logger(err as Error, { context: "GA4 config: Error loading accounts" });
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('insufficient') || message.includes('scope') || message.includes('403') || message.includes('401')) {
        setAccountsError("Insufficient permissions. Please re-authorize your Google Analytics account and try again. If the issue persists, contact support.");
      } else {
        setAccountsError("Failed to load accounts. Please try again.");
      }
    } finally {
      setLoadingAccounts(false);
    }
  }, [config.credentialId]);

  // Load the account list whenever the surface becomes active. The dialog clears
  // the selection on (re)open; the inline panel keeps its pre-loaded selection.
  useEffect(() => {
    if (!active) return;
    if (!isInline) {
      setSelectedAccountIds([]);
      setSelectedPropertyIds([]);
      setProperties([]);
    }
    fetchAccounts();
  }, [active, isInline, fetchAccounts]);

  // Inline: pre-load the saved selection once the credential data is available.
  const initedRef = useRef(false);
  useEffect(() => {
    if (!isInline || initedRef.current || !credentialsLoaded) return;
    initedRef.current = true;
    pendingPropertyRef.current = savedPropertyIds;
    setSelectedAccountIds(savedAccountIds);
  }, [isInline, credentialsLoaded, savedAccountIds, savedPropertyIds]);

  // Load properties when accounts are selected.
  const fetchProperties = useCallback(async () => {
    if (selectedAccountIds.length === 0) {
      setProperties([]);
      setSelectedPropertyIds([]);
      // NOTE: do NOT clear pendingPropertyRef here. On the inline pre-load,
      // accounts are seeded asynchronously, so this effect fires once with an
      // empty selection *after* the pre-load already stashed the saved
      // properties — clearing here would wipe them before they can be restored
      // (the saved property selection would vanish on refresh). The ref is
      // cleared once, right after a successful restore below.
      return;
    }

    setLoadingProperties(true);
    setPropertiesError(null);
    try {
      const propertyPromises = selectedAccountIds.map((accountId) =>
        executeAppAction(
          BASE_URL,
          "google_analytics",
          "GOOGLE_ANALYTICS_LIST_PROPERTIES_FILTERED",
          [{ id: config.credentialId }],
          [{ filter: `parent:${accountId}` }]
        ).then((res) => parsePropertiesResponse(res, accountId))
      );

      const allProperties = (await Promise.all(propertyPromises)).flat();

      if (allProperties.length === 0) {
        setPropertiesError("No properties found for the selected accounts.");
      }
      setProperties(allProperties);
      // Restore a pre-loaded selection (inline) once its properties are available.
      if (pendingPropertyRef.current) {
        const target = pendingPropertyRef.current;
        pendingPropertyRef.current = null;
        setSelectedPropertyIds(target.filter((id) => allProperties.some((p) => p.id === id)));
      } else {
        setSelectedPropertyIds([]);
      }
    } catch (err) {
      logger(err as Error, { context: "GA4 config: Error loading properties" });
      const message = err instanceof Error ? err.message.toLowerCase() : '';
      if (message.includes('insufficient') || message.includes('scope')) {
        setPropertiesError("Insufficient permissions. Please re-authorize your Google Analytics account and try again. If the issue persists, contact support.");
      } else {
        setPropertiesError("Failed to load properties. Please try again.");
      }
    } finally {
      setLoadingProperties(false);
    }
  }, [config.credentialId, selectedAccountIds]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleSave = async () => {
    const selectedAccounts = accounts
      .filter((a) => selectedAccountIds.includes(a.id))
      .map((a) => ({ id: a.id, name: a.name }));

    const selectedProperties = properties
      .filter((p) => selectedPropertyIds.includes(p.id))
      .map((p) => ({ id: p.id, name: p.name }));

    try {
      await patchCustomData.mutateAsync({
        credentialId: config.credentialId,
        customData: {
          accounts: selectedAccounts,
          properties: selectedProperties,
        },
      });

      toast({
        title: "Configuration saved",
        description: `${config.displayName} has been configured with ${selectedAccounts.length} ${selectedAccounts.length === 1 ? "account" : "accounts"} and ${selectedProperties.length} ${selectedProperties.length === 1 ? "property" : "properties"}.`,
      });
      onComplete();
    } catch (err) {
      logger(err as Error, { context: "GA4 config: Error saving custom data" });
      toast({
        title: "Error",
        description: "Failed to save configuration. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Require a valid selection AND a change from what's saved — so Save is
  // disabled when nothing was picked, and after a save (selection == saved).
  const dirty =
    !sameIdSet(selectedAccountIds, savedAccountIds) || !sameIdSet(selectedPropertyIds, savedPropertyIds);
  const canSave = selectedAccountIds.length > 0 && selectedPropertyIds.length > 0 && dirty;

  const accountOptions = accounts.map((a) => ({ label: a.name, value: a.id }));
  const propertyOptions = properties.map((p) => ({ label: p.name, value: p.id }));

  const body = (
    <>
      {/* Accounts */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Accounts</Label>
        {loadingAccounts ? (
          <div className="flex items-center gap-2 py-4 justify-center text-sm text-custom-text-primary opacity-50">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading accounts...
          </div>
        ) : accountsError ? (
          <div className="space-y-2 py-2">
            <p className="text-sm text-red-500">{accountsError}</p>
            <Button variant="outline" size="sm" onClick={fetchAccounts}>
              Retry
            </Button>
          </div>
        ) : (
          <MultiSelect
            options={accountOptions}
            defaultValue={[]}
            value={selectedAccountIds}
            onValueChange={setSelectedAccountIds}
            placeholder="Select accounts"
            disablePortal
          />
        )}
      </div>

      {/* Properties */}
      {selectedAccountIds.length > 0 && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">Properties</Label>
          {loadingProperties ? (
            <div className="flex items-center gap-2 py-4 justify-center text-sm text-custom-text-primary opacity-50">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading properties...
            </div>
          ) : propertiesError ? (
            <div className="space-y-2 py-2">
              <p className="text-sm text-red-500">{propertiesError}</p>
              <Button variant="outline" size="sm" onClick={fetchProperties}>
                Retry
              </Button>
            </div>
          ) : (
            <MultiSelect
              options={propertyOptions}
              defaultValue={[]}
              value={selectedPropertyIds}
              onValueChange={setSelectedPropertyIds}
              placeholder="Select properties"
              disablePortal
            />
          )}
        </div>
      )}
    </>
  );

  if (isInline) {
    return (
      <InlineConfigPanel
        title={`Configure ${config.displayName}`}
        description="Select the accounts and properties you want to sync data from."
        onSave={handleSave}
        canSave={canSave}
        saving={patchCustomData.isPending}
      >
        <div className="space-y-5">{body}</div>
      </InlineConfigPanel>
    );
  }

  return (
    <Dialog open={!!open} onOpenChange={(isOpen) => { if (!isOpen) onComplete(); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Configure {config.displayName}</DialogTitle>
          <DialogDescription>
            Select the accounts and properties you want to sync data from.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">{body}</div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onComplete}>
            Skip
          </Button>
          <Button
            variant="black"
            onClick={handleSave}
            disabled={!canSave || patchCustomData.isPending}
          >
            {patchCustomData.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
